import { useCallback } from 'react';
import { useDialog } from '../context/DialogContext';

export interface DuplicateNameGuardArgs {
  /** Entity noun shown in the copy, e.g. "vendor" or "catalog item". */
  entityLabel: string;
  /** The name the user is about to create. */
  name: string;
  /** Optional extra context, e.g. 'in categories: Catering, Decor'. */
  detail?: string;
}

/**
 * Warning shown before creating an entity whose name already exists.
 * Call it only when a duplicate was found; resolves true when the user
 * chooses to create another entity with the same name anyway.
 */
export function useDuplicateNameGuard() {
  const { confirm } = useDialog();

  return useCallback(
    ({ entityLabel, name, detail }: DuplicateNameGuardArgs): Promise<boolean> =>
      confirm({
        title: 'Duplicate Name',
        message: `A ${entityLabel} named "${name}" already exists${detail ? ` ${detail}` : ''}. Create another ${entityLabel} with the same name?`,
        variant: 'warning',
        confirmLabel: 'Create Anyway',
      }),
    [confirm],
  );
}

/** Case-insensitive name equality used for duplicate detection. */
export function isSameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
